import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button
} from '@invent/wl-ui-kit-next/ui';
import { Pencil, Plus } from 'lucide-react';
import * as React from 'react';

import { CriteriaThresholdsSlot } from '@/demo';
import { FieldGrid, FieldGridItem, RecordSection, StatusChip } from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { formatDateTime } from '@/domains/survey';
import type { IConcernCriterion } from '../../api/work-item-contracts';
import { workItemsApi } from '../../api/work-items-api';
import { AdminPage, LoadError, TableSkeleton, useAdminData } from '../../components/admin-page';
import { readPredicate, ruleWords, usedValues } from '../../concern-predicate';
import { refusalMessage } from '../../refusal';
import { criterionKindLabel } from '../../work-items';
import { CriterionForm, criterionGone } from './criterion-form';
import { NoteLine, sentence, unanswered, type ISaidNote } from '../person-hub/said';
import { RuleWords, TechnicalDefinition } from './rule-words';

/**
 * Concern criteria — what raises a work item (SR-222, F9's R13; CMP-08), at
 * `/team/setup/criteria`, admin alone: survey-api lets staff read the criteria, but writes them
 * under `compliance.configure`, admin's, and Setup is where admin configures.
 *
 * **Every version is drawn, in survey-api's order, and nothing here sorts or judges.** The list
 * route answers the seed's codes in seed order then the others by code, each code's current
 * version first, so a code's section is its current version with the older ones folded under
 * "Earlier versions" — an item names the version that raised it (`name · v<n>` on the pane), and
 * that version has to be findable.
 *
 * **Each rule is said in words, and its stored form is one press away** (SR-227, design
 * `docs/superpowers/specs/2026-10-05-concern-criteria-builder-ui.md` §3.2). The reader is
 * compliance staff, not an engineer, so the words come first (`RuleWords`, over
 * `concern-predicate.ts`' mirror of the parser's grammar) and the stored body sits under "Show the
 * technical definition", read-only, for an auditor or an engineer. A shape the mirror cannot read
 * — a member the parser grew before the mirror did — is never put into guessed words: its
 * technical definition is drawn open where the words would be, under a sentence saying so. The
 * page's copy says "the system", never survey-api: its reader does not know the name.
 *
 * **A write appends a version and re-reads; nothing deletes.** Edit opens from the code's own
 * read (B8), so the form starts from the version that is current now rather than the list's copy;
 * Disable / Enable is a confirmed `PUT` of that current version with `enabled` flipped, sent only
 * when the fresh read is still the version drawn — a criterion someone revised meanwhile is said
 * and read again, never flipped from a copy the page no longer holds. survey-api takes no
 * `If-Match` here: two saves at once collide on the version's uniqueness, its 409 `code-in-use`,
 * which the form says. What a write was told is held by the page per code, the hub's rule, since
 * the re-read after it redraws the section.
 *
 * With the demo on, the in-consideration thresholds — which survey-api has no route for — are a
 * badged mock panel after the criteria (`CriteriaThresholdsSlot`); with it off, nothing.
 */
export function AdminConcernCriteriaPage() {
  const { data, error, reload } = useAdminData<IConcernCriterion[]>((signal) =>
    workItemsApi.listConcernCriteria(signal)
  );
  const [adding, setAdding] = React.useState(false);
  const [said, setSaid] = React.useState<Record<string, ISaidNote>>({});
  const [stale, setStale] = React.useState<Nullable<string>>(null);

  const say = (code: string, note: ISaidNote) => setSaid((held) => ({ ...held, [code]: note }));

  /** A save landed: said beside its code, and the list read again. */
  const saved = async (version: IConcernCriterion) => {
    setStale(null);
    const read = await reload();
    say(version.code, {
      tone: 'done',
      text:
        `Saved — version ${version.version} is current` +
        (read ? '.' : '. The criteria could not be read again — reload to see them.')
    });
  };

  /** Something the page holds is out of date: said above the list, the list read again. */
  const readAgain = (text: string) => {
    setStale(text);
    void reload();
  };

  const codes = React.useMemo(() => byCode(data ?? []), [data]);
  const suggestions = React.useMemo(() => usedValues(data ?? []), [data]);

  return (
    <AdminPage
      title="Concern criteria"
      crumb="Setup"
      actions={
        <Button type="button" size="sm" disabled={adding} onClick={() => setAdding(true)}>
          <Plus className="size-4" aria-hidden />
          Add a criterion
        </Button>
      }
    >
      <p className="max-w-3xl text-[0.8125rem] text-muted-foreground">
        A concern criterion is a rule Veritant applies to every disclosure BrokerCheck reports. When
        a disclosure — or a person’s set of disclosures — meets a rule, a work item is raised in the
        compliance queue. Saving a change makes a new version; items already raised keep the version
        that raised them.
      </p>

      {adding && (
        <section
          aria-label="New criterion"
          className="rounded-lg border border-border bg-white p-5"
        >
          <CriterionForm
            mode="create"
            onSaved={(version) => {
              setAdding(false);
              void saved(version);
            }}
            onStale={readAgain}
            onCancel={() => setAdding(false)}
            suggestions={suggestions}
          />
        </section>
      )}

      {stale && (
        <Alert variant="destructive">
          <AlertDescription>{stale}</AlertDescription>
        </Alert>
      )}

      {error && data === null ? (
        <LoadError message={error} />
      ) : data === null ? (
        <TableSkeleton rows={4} />
      ) : (
        <>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>
                Could not refresh — showing the criteria as last read. {sentence(error)}
              </AlertDescription>
            </Alert>
          )}
          {codes.length === 0 ? (
            <p className="text-[0.8125rem] text-muted-foreground">There is no criterion yet.</p>
          ) : (
            <div className="grid gap-3">
              {codes.map(([current, ...earlier]) => (
                <CriterionSection
                  key={current.code}
                  current={current}
                  earlier={earlier}
                  note={said[current.code]}
                  onSay={(note) => say(current.code, note)}
                  onSaved={saved}
                  onStale={readAgain}
                  suggestions={suggestions}
                />
              ))}
            </div>
          )}
        </>
      )}

      <CriteriaThresholdsSlot />
    </AdminPage>
  );
}

/** The versions grouped by code, in the order survey-api listed the codes, each code's versions
 *  as sent — its current one first. */
function byCode(versions: IConcernCriterion[]): IConcernCriterion[][] {
  const groups = new Map<string, IConcernCriterion[]>();
  for (const version of versions) {
    const group = groups.get(version.code);
    if (group) group.push(version);
    else groups.set(version.code, [version]);
  }
  return [...groups.values()];
}

function queuesText(queues: Nullable<string[]>): string {
  return queues === null ? 'Every queue' : queues.join(', ');
}

function EnabledChip({ enabled }: { enabled: boolean }) {
  return <StatusChip tone={enabled ? 'success' : 'neutral'}>{enabled ? 'Enabled' : 'Disabled'}</StatusChip>;
}


function CriterionSection({
  current,
  earlier,
  note,
  onSay,
  onSaved,
  onStale,
  suggestions
}: {
  current: IConcernCriterion;
  earlier: IConcernCriterion[];
  note?: ISaidNote;
  onSay: (note: ISaidNote) => void;
  onSaved: (version: IConcernCriterion) => void;
  onStale: (text: string) => void;
  suggestions: ReadonlyMap<string, readonly string[]>;
}) {
  const [editing, setEditing] = React.useState<Nullable<IConcernCriterion>>(null);
  const [reading, setReading] = React.useState(false);
  const [flipping, setFlipping] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const flip = current.enabled ? 'Disable' : 'Enable';

  /** The code's current version, read afresh — or `null` where the page said why not. */
  const readCurrent = async (): Promise<Nullable<IConcernCriterion>> => {
    try {
      const fresh = await workItemsApi.getConcernCriterion(current.code);
      if (fresh === null) {
        onStale(criterionGone(current.code));
      }
      return fresh;
    } catch (error) {
      if (unanswered(error)) {
        onStale(`The criterion "${current.code}" could not be read — the criteria have been read again.`);
      } else {
        onSay({ tone: 'refused', text: sentence(refusalMessage(error, 'The criterion could not be read')) });
      }
      return null;
    }
  };

  const edit = async () => {
    setReading(true);
    const fresh = await readCurrent();
    setReading(false);
    if (fresh) setEditing(fresh);
  };

  const toggle = async () => {
    setFlipping(true);
    const fresh = await readCurrent();
    if (fresh === null) {
      setFlipping(false);
      setConfirming(false);
      return;
    }
    if (fresh.version !== current.version) {
      setFlipping(false);
      setConfirming(false);
      onStale(
        `${current.name} changed since the page read it — version ${fresh.version} is current now. ` +
          'The criteria have been read again; nothing was saved.'
      );
      return;
    }
    try {
      const saved = await workItemsApi.reviseConcernCriterion(current.code, {
        name: fresh.name,
        kind: fresh.kind,
        predicate: fresh.predicate,
        queues: fresh.queues,
        enabled: !fresh.enabled
      });
      onSaved(saved);
    } catch (error) {
      if (unanswered(error)) {
        onStale(
          `The answer did not arrive for ${current.name} — the criteria have been read again: check ` +
            'whether it changed.'
        );
      } else if (error instanceof ApiError && error.isProblem(ProblemType.codeInUse)) {
        onStale(
          `Someone saved a version of ${current.name} at the same moment — the criteria have been ` +
            'read again; nothing was saved.'
        );
      } else if (error instanceof ApiError && error.status === 404) {
        onStale(criterionGone(current.code));
      } else {
        onSay({
          tone: 'refused',
          title: `Not ${current.enabled ? 'disabled' : 'enabled'}`,
          text: sentence(refusalMessage(error, 'The change was refused'))
        });
      }
    } finally {
      setFlipping(false);
      setConfirming(false);
    }
  };

  return (
    <div data-criterion={current.code}>
      <RecordSection
        page="concern-criteria"
        sectionKey={`criterion-${current.code}`}
        title={current.name}
        level={2}
        defaultOpen
        filled={0}
        total={0}
      >
        {editing ? (
          <CriterionForm
            mode="edit"
            initial={editing}
            onSaved={(version) => {
              setEditing(null);
              onSaved(version);
            }}
            onStale={(text) => {
              setEditing(null);
              onStale(text);
            }}
            onCancel={() => setEditing(null)}
            suggestions={suggestions}
          />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <EnabledChip enabled={current.enabled} />
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={reading || flipping}
                aria-label={`Edit ${current.name}`}
                onClick={() => void edit()}
              >
                <Pencil className="size-3.5" aria-hidden />
                {reading ? 'Reading…' : 'Edit'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={reading || flipping}
                aria-label={`${flip} ${current.name}`}
                onClick={() => setConfirming(true)}
              >
                {flip}
              </Button>
            </div>
            {note && <NoteLine note={note} />}
            <VersionRule version={current} />
            {earlier.length > 0 && (
              <RecordSection
                page="concern-criteria"
                sectionKey={`earlier-${current.code}`}
                title={`Earlier versions (${earlier.length})`}
                level={3}
                defaultOpen={false}
                filled={0}
                total={0}
              >
                <ol className="grid gap-4" aria-label={`Earlier versions of ${current.name}`}>
                  {earlier.map((version) => (
                    <li key={version.criterionId} data-version={version.version} className="grid gap-2">
                      <div className="flex flex-wrap items-center gap-2 text-[0.8125rem]">
                        <span className="font-medium">
                          v{version.version} · {version.name}
                        </span>
                        <EnabledChip enabled={version.enabled} />
                      </div>
                      <VersionRule version={version} />
                    </li>
                  ))}
                </ol>
              </RecordSection>
            )}
          </>
        )}
      </RecordSection>

      <AlertDialog open={confirming} onOpenChange={(open) => !flipping && setConfirming(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {flip} {current.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {current.enabled
                ? 'This saves a new version, disabled, the rest unchanged: from then on it raises no item. Items it raised stay in their queues.'
                : 'This saves a new version, enabled, the rest unchanged: from then on a BrokerCheck answer that meets it raises an item.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={flipping}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={flipping}
              onClick={(event) => {
                // Held open until the answer is in, as the hub's confirmations are.
                event.preventDefault();
                void toggle();
              }}
            >
              {flipping ? 'Saving…' : flip}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** What the reader of a stored version is told by the definition note. */
const STORED_NOTE = 'How the system stores this rule. It cannot be edited here.';

/**
 * One version: its rule in words, its facts, and its technical definition behind a link — or, for
 * a shape the words cannot say, the definition drawn open where the words would be.
 */
function VersionRule({ version }: { version: IConcernCriterion }) {
  const tree = React.useMemo(
    () => readPredicate(version.kind, version.predicate),
    [version.kind, version.predicate]
  );
  if (tree === null) {
    return (
      <>
        <p className="text-[0.8125rem]">
          This rule’s conditions are in a form this page cannot put into words — shown as the system
          stores them.
        </p>
        <TechnicalDefinition value={version.predicate} note={STORED_NOTE} alwaysOpen />
        <VersionFacts version={version} />
      </>
    );
  }
  return (
    <>
      <RuleWords words={ruleWords(version.kind, tree)} />
      <VersionFacts version={version} />
      <TechnicalDefinition value={version.predicate} note={STORED_NOTE} />
    </>
  );
}

function VersionFacts({ version }: { version: IConcernCriterion }) {
  return (
    <FieldGrid>
      <FieldGridItem label="Code" filled>
        <span className="font-mono">{version.code}</span>
      </FieldGridItem>
      <FieldGridItem label="Version" filled>
        v{version.version}
      </FieldGridItem>
      <FieldGridItem label="Kind" filled>
        {criterionKindLabel(version.kind)}{' '}
        <span className="font-mono text-xs text-muted-foreground">{version.kind}</span>
      </FieldGridItem>
      <FieldGridItem label="Queues" filled>
        {queuesText(version.queues)}
      </FieldGridItem>
      <FieldGridItem label="Effective" filled>
        {formatDateTime(version.effectiveFrom)}
        {version.supersededAt && <> — superseded {formatDateTime(version.supersededAt)}</>}
      </FieldGridItem>
      <FieldGridItem label="Author" filled>
        {version.author ? version.author.email : 'Seeded'}
      </FieldGridItem>
    </FieldGrid>
  );
}
